import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Button, 
  Alert,
  Modal,
  Spinner,
  Badge,
  Table,
  Form,
  ButtonGroup,
  Carousel,
  ListGroup
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RetryImage } from 'components/common/RetryImage';
import { ImageRotateControls, useImageRotation } from 'components/modals/ImageRotateControls';
import { faArrowLeft, faEdit, faUser, faCar, faCamera, faPlus, faUpload, faSync, faTimes, faSearch, faFilePdf, faExclamationTriangle, faWrench, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from 'utils/axiosInterceptors';
import { CarMakeSelect } from 'components/vehicles/CarMakeSelect';
import { CarModelSelect } from 'components/vehicles/CarModelSelect';
import { CarModelBodyTypeSelect, carModelBodyTypeLabel } from 'components/vehicles/CarModelBodyTypeSelect';
import { useBranchContext } from 'providers/BranchProvider';
import ComplaintEditor, { extractComplaintPlainText } from 'components/editors/ComplaintEditor';
import { toast } from 'react-toastify';
import { toastConfig } from 'utils/toastConfig';
import { AxiosError } from 'axios';
import HtmlPdfPreviewModal from 'components/modals/HtmlPdfPreviewModal';
import {
  DocumentPreview,
  parseDocumentPreviewResponse,
  revokeDocumentPreview
} from 'utils/printHtmlAsPdf';

// Types - Based on VehicleDetailDto structure
/** Matches API VehicleImageType.Vehicle */
const VEHICLE_IMAGE_TYPE_VEHICLE = 1;

interface VehicleImageDto {
  vehicleImageId: number;
  vehicleId: number;
  googleFileId: string;
  contentType: string;
  filename: string;
  vehicleImageCategoryId?: number;
  vehicleImageCategoryName?: string;
  vehicleImageType?: number | string;
  hasNonVehicleWarning?: boolean;
  nonVehicleWarningReason?: string;
}

interface VehicleImageUploadWarningDto {
  fileName: string;
  message: string;
  isNonVehicle: boolean;
}

interface AddVehicleImagesResultDto {
  success: boolean;
  warnings: VehicleImageUploadWarningDto[];
}

interface VehicleDetailDto {
  vehicleId: number;
  plateNumber?: string;
  conductionNumber?: string;
  chassisNo?: string;
  carMakeId: number;
  carMake: string;
  carModelId: number;
  carModel: string;
  bodyType?: string;
  year: number;
  customerId?: number;
  customerName?: string;
  customerReferrer?: string | null;
  vehicleImages: VehicleImageDto[];
  color?: string;
  fuelType?: string;
  transmission?: string;
}

interface Customer {
  customerId: number;
  customerName: string;
  email: string;
  contactNumber: string;
}

interface EditVehicleRequest {
  PlateNumber?: string;
  ConductionNumber?: string;
  ChassisNo?: string;
  CarMake: string;
  CarModel: string;
  Year: number;
  CustomerId?: number;
  Color?: string;
  FuelType?: string;
  Transmission?: string;
  BodyType?: string;
}

interface VehicleServiceDto {
  vehicleServiceId: number;
  vehicleId: number;
  currentMileage: number;
  createdOn: string;
  customerRequestComplaint: string;
  customerRequestComplaintPlainText?: string;
  serviceTitle?: string;
  serviceResolution?: string;
  status: 'New' | 'InProgress' | 'Submitted';
  afterServiceDetails?: string;
  paymentStatus?: 'Unpaid' | 'Paid';
  paymentMethodDisplay?: string;
  hasServiceSummary?: boolean;
  hasPaymentProof?: boolean;
  mileageMissing?: boolean;
}

interface CreateVehicleServiceRequest {
  currentMileage: number;
  customerRequestComplaint: string;
  serviceTitle?: string;
}

const VehicleDetails: React.FC = () => {
  const navigate = useNavigate();
  const { vehicleId } = useParams<{ vehicleId: string }>();
  const { selectedBranch } = useBranchContext();
  
  // State
  const [vehicle, setVehicle] = useState<VehicleDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const vehiclePhotos = (vehicle?.vehicleImages || []).filter(
    img =>
      img.vehicleImageType === VEHICLE_IMAGE_TYPE_VEHICLE ||
      img.vehicleImageType === 'Vehicle' ||
      img.vehicleImageCategoryName === 'Vehicle'
  );
  const currentVehiclePhoto =
    vehiclePhotos[Math.min(selectedImageIndex, Math.max(vehiclePhotos.length - 1, 0))];
  const imageRotation = useImageRotation(
    showImageModal ? currentVehiclePhoto?.googleFileId : undefined
  );
  
  // Upload state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadWarning, setUploadWarning] = useState<string | null>(null);
  
  // Edit state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState<EditVehicleRequest>({
    PlateNumber: '',
    ConductionNumber: '',
    ChassisNo: '',
    CarMake: '',
    CarModel: '',
    Year: new Date().getFullYear(),
    CustomerId: undefined,
    Color: '',
    FuelType: '',
    Transmission: '',
    BodyType: ''
  });
  const [editLoading, setEditLoading] = useState(false);
  
  // Service state
  const [services, setServices] = useState<VehicleServiceDto[]>([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [serviceFormData, setServiceFormData] = useState<CreateVehicleServiceRequest>({
    currentMileage: 0,
    customerRequestComplaint: '',
    serviceTitle: ''
  });
  const [serviceLoading, setServiceLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  
  // Customer lookup state for editing
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerLoading, setCustomerLoading] = useState(false);
  
  // Camera state
  const [cameraMode, setCameraMode] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [availableCameras, setAvailableCameras] = useState<MediaDeviceInfo[]>([]);
  const [currentCameraId, setCurrentCameraId] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);
  const [invoicePreview, setInvoicePreview] = useState<DocumentPreview | null>(null);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [showMileageConfirmModal, setShowMileageConfirmModal] = useState(false);
  const [savingReferrer, setSavingReferrer] = useState(false);
  const [showEditReferrerModal, setShowEditReferrerModal] = useState(false);
  const [editReferrer, setEditReferrer] = useState('');

  // Fetch vehicle details
  const fetchVehicleDetails = async () => {
    if (!vehicleId) {
      setError('Vehicle ID not provided');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await axiosInstance.get(`/api/v1/vehicles/${vehicleId}`);
      console.log('Vehicle API response:', response.data);
      setVehicle(response.data);
    } catch (err) {
      console.error('Error fetching vehicle details:', err);
      setError('Failed to load vehicle details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVehicleDetails();
    if (selectedBranch?.branchDomain) {
      fetchVehicleServices();
    }
  }, [vehicleId, selectedBranch?.branchDomain]);

  // Fetch vehicle services
  const fetchVehicleServices = async () => {
    if (!vehicleId || !selectedBranch?.branchDomain) return;

    try {
      setServicesLoading(true);
      const response = await axiosInstance.get(`/api/v1/${selectedBranch.branchDomain}/vehicles/${vehicleId}/services`);
      setServices(response.data);
    } catch (err) {
      console.error('Error fetching vehicle services:', err);
    } finally {
      setServicesLoading(false);
    }
  };

  // Handle create service
  const createService = async () => {
    if (!vehicleId || !selectedBranch?.branchDomain) return;

    const plain = extractComplaintPlainText(serviceFormData.customerRequestComplaint);
    if (!plain) {
      toast.error('Customer concern is required', toastConfig);
      return;
    }
    if (plain.length > 500) {
      toast.error('Customer concern cannot exceed 500 characters', toastConfig);
      return;
    }

    const trimmedTitle = serviceFormData.serviceTitle?.trim() || '';
    if (trimmedTitle.length > 80) {
      toast.error('Service title cannot exceed 80 characters', toastConfig);
      return;
    }

    try {
      setServiceLoading(true);
      setShowMileageConfirmModal(false);

      await axiosInstance.post(`/api/v1/${selectedBranch.branchDomain}/vehicles/${vehicleId}/services`, {
        currentMileage: serviceFormData.currentMileage,
        customerRequestComplaint: serviceFormData.customerRequestComplaint,
        ...(trimmedTitle ? { serviceTitle: trimmedTitle } : {})
      });

      setServiceFormData({
        currentMileage: 0,
        customerRequestComplaint: '',
        serviceTitle: ''
      });
      setShowServiceModal(false);
      toast.success('Service created', toastConfig);
      await fetchVehicleServices();
    } catch (err) {
      console.error('Error creating service:', err);
      if (err instanceof AxiosError) {
        toast.error(err.response?.data?.errorMessage || 'Failed to create service', toastConfig);
      } else {
        toast.error('Failed to create service', toastConfig);
      }
    } finally {
      setServiceLoading(false);
    }
  };

  const handleCreateService = async () => {
    if (!vehicleId || !selectedBranch?.branchDomain) return;

    const plain = extractComplaintPlainText(serviceFormData.customerRequestComplaint);
    if (!plain) {
      toast.error('Customer concern is required', toastConfig);
      return;
    }
    if (plain.length > 500) {
      toast.error('Customer concern cannot exceed 500 characters', toastConfig);
      return;
    }
    if (serviceFormData.currentMileage <= 0) {
      setShowMileageConfirmModal(true);
      return;
    }

    await createService();
  };

  const handleViewSummary = async (serviceId: number) => {
    if (!vehicleId || !selectedBranch?.branchDomain) return;
    try {
      const res = await axiosInstance.get(
        `/api/v1/${selectedBranch.branchDomain}/vehicles/${vehicleId}/services/${serviceId}/summary`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
    } catch (err) {
      if (err instanceof AxiosError) {
        toast.error('Service summary is not available yet', toastConfig);
      } else {
        toast.error('Failed to open summary', toastConfig);
      }
    }
  };

  const closeInvoicePreview = () => {
    setShowInvoicePreview(false);
    setInvoicePreview(prev => {
      revokeDocumentPreview(prev);
      return null;
    });
  };

  const handleDownloadInvoice = async (serviceId: number) => {
    if (!vehicleId || !selectedBranch?.branchDomain) return;
    setInvoicePreview(prev => {
      revokeDocumentPreview(prev);
      return null;
    });
    setShowInvoicePreview(true);
    setGeneratingInvoice(true);
    try {
      const response = await axiosInstance.get(
        `/api/v1/${selectedBranch.branchDomain}/vehicles/${vehicleId}/services/${serviceId}/invoice`,
        { responseType: 'blob' }
      );
      const preview = await parseDocumentPreviewResponse(
        response,
        `customer-copy-service-${serviceId}.pdf`
      );
      setInvoicePreview(preview);
    } catch (err) {
      closeInvoicePreview();
      if (err instanceof AxiosError && err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text) as { errorMessage?: string };
          toast.error(parsed.errorMessage || 'Failed to generate customer copy', toastConfig);
          return;
        } catch {
          // fall through
        }
      }
      toast.error('Failed to generate customer copy', toastConfig);
    } finally {
      setGeneratingInvoice(false);
    }
  };

  // Debounced search effect for customers
  useEffect(() => {
    if (customerSearch.length > 0) {
      const timer = setTimeout(() => {
        searchCustomers(customerSearch);
      }, 500); // 500ms debounce

      return () => clearTimeout(timer);
    } else {
      setCustomers([]);
    }
  }, [customerSearch]);

  // Search customers
  const searchCustomers = async (search: string) => {
    try {
      setCustomerLoading(true);
      const response = await axiosInstance.get(`/api/v1/customers?search=${encodeURIComponent(search)}&pageSize=50`);
      setCustomers(response.data);
    } catch (error: unknown) {
      console.error('Error searching customers:', error);
    } finally {
      setCustomerLoading(false);
    }
  };

  // Handle customer selection
  const handleCustomerSelect = (customer: Customer) => {
    setSelectedCustomer(customer);
    setEditFormData(prev => ({
      ...prev,
      CustomerId: customer.customerId
    }));
    setShowCustomerModal(false);
    setCustomerSearch('');
    setCustomers([]);
  };

  // Open edit modal
  const openEditModal = () => {
    if (!vehicle) return;
    
    setEditFormData({
      PlateNumber: vehicle.plateNumber || '',
      ConductionNumber: vehicle.conductionNumber || '',
      ChassisNo: vehicle.chassisNo || '',
      CarMake: vehicle.carMake || '',
      CarModel: vehicle.carModel || '',
      Year: vehicle.year || new Date().getFullYear(),
             CustomerId: vehicle.customerId,
      Color: vehicle.color || '',
      
      FuelType: vehicle.fuelType || '',
      Transmission: vehicle.transmission || '',
      BodyType: vehicle.bodyType || ''
    });
    
    // Set selected customer if exists
    if (vehicle.customerId && vehicle.customerName) {
      setSelectedCustomer({
                 customerId: vehicle.customerId,
        customerName: vehicle.customerName,
        email: '',
        contactNumber: ''
      });
    } else {
      setSelectedCustomer(null);
    }
    
    setShowEditModal(true);
    setEditError(null);
  };

  // Handle edit form input changes
  const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: name === 'Year' ? parseInt(value) || 0 : value
    }));
  };

  // Handle edit form submission
  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!vehicleId) return;
    
    // Validate that at least one of PlateNumber or ConductionNumber is provided
    if (!editFormData.PlateNumber?.trim() && !editFormData.ConductionNumber?.trim()) {
      setEditError('Either Plate Number or Conduction Number is required');
      return;
    }
    
    setEditLoading(true);
    setEditError(null);
    
    try {
      // Filter out empty optional fields
      const submitData = { ...editFormData };
      if (!submitData.PlateNumber?.trim()) {
        delete submitData.PlateNumber;
      }
      if (!submitData.ConductionNumber?.trim()) {
        delete submitData.ConductionNumber;
      }
      if (!submitData.ChassisNo?.trim()) {
        delete submitData.ChassisNo;
      }
      if (!submitData.Color?.trim()) {
        delete submitData.Color;
      }
      
      if (!submitData.FuelType?.trim()) {
        delete submitData.FuelType;
      }
      if (!submitData.Transmission?.trim()) {
        delete submitData.Transmission;
      }
      if (!submitData.BodyType?.trim()) {
        delete submitData.BodyType;
      }
      if (!submitData.CustomerId) {
        delete submitData.CustomerId;
      }
      
      await axiosInstance.put(`/api/v1/vehicles/${vehicleId}`, submitData);
      
      // Close modal and refresh vehicle details
      setShowEditModal(false);
      await fetchVehicleDetails();
      
    } catch (error: unknown) {
      console.error('Edit error:', error);
      
      let errorMessage = 'An error occurred while updating the vehicle';
      
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { 
          response?: { 
            status?: number; 
            data?: { 
              message?: string; 
              errors?: Record<string, string[]>; 
            } | string; 
          }; 
          message?: string; 
        };
        
        if (axiosError.response?.status === 400) {
          // Handle 400 Bad Request errors
          if (typeof axiosError.response.data === 'object' && axiosError.response.data?.message) {
            errorMessage = axiosError.response.data.message;
          } else if (typeof axiosError.response.data === 'object' && axiosError.response.data?.errors) {
            // Handle validation errors
            const errors = axiosError.response.data.errors;
            errorMessage = Object.values(errors).flat().join(', ');
          } else if (typeof axiosError.response.data === 'string') {
            errorMessage = axiosError.response.data;
          }
        } else if (typeof axiosError.response?.data === 'object' && axiosError.response.data?.message) {
          errorMessage = axiosError.response.data.message;
        } else if (axiosError.message) {
          errorMessage = axiosError.message;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      setEditError(errorMessage);
    } finally {
      setEditLoading(false);
    }
  };

  // Close edit modal
  const closeEditModal = () => {
    setShowEditModal(false);
    setEditError(null);
    setSelectedCustomer(null);
    setCustomerSearch('');
    setCustomers([]);
  };

  // Cleanup camera stream on unmount
  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [cameraStream]);

  // Handle image click
  const handleImageClick = (index: number = 0) => {
    setSelectedImageIndex(index);
    setShowImageModal(true);
  };

  const handleDeleteImage = async (image: VehicleImageDto) => {
    const currentIndex = images.findIndex(
      item => item.vehicleImageId === image.vehicleImageId
    );
    const remainingCount = images.length - 1;

    try {
      await axiosInstance.delete(
        `/api/v1/vehicles/images/${image.googleFileId}`
      );
      toast.success('Image deleted', { ...toastConfig, autoClose: 2000 });
      await fetchVehicleDetails();

      if (remainingCount === 0) {
        setShowImageModal(false);
        setSelectedImageIndex(0);
      } else if (currentIndex >= remainingCount) {
        setSelectedImageIndex(remainingCount - 1);
      } else if (currentIndex >= 0) {
        setSelectedImageIndex(currentIndex);
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data?.errorMessage || 'Delete failed', {
          ...toastConfig,
          autoClose: 3000
        });
      }
    }
  };

  // Detect available cameras
  const detectCameras = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      setAvailableCameras(videoDevices);
      
      // Set default camera (prefer back camera if available)
      const backCamera = videoDevices.find(device => 
        device.label.toLowerCase().includes('back') || 
        device.label.toLowerCase().includes('rear') ||
        device.label.toLowerCase().includes('environment')
      );
      
      if (backCamera) {
        setCurrentCameraId(backCamera.deviceId);
        setFacingMode('environment');
      } else if (videoDevices.length > 0) {
        setCurrentCameraId(videoDevices[0].deviceId);
        setFacingMode('user');
      }
      
      console.log('Available cameras:', videoDevices);
    } catch (error: unknown) {
      console.error('Error detecting cameras:', error);
    }
  };

  // Switch camera
  const switchCamera = () => {
    if (availableCameras.length < 2) return;
    
    const currentIndex = availableCameras.findIndex(camera => camera.deviceId === currentCameraId);
    const nextIndex = (currentIndex + 1) % availableCameras.length;
    const nextCamera = availableCameras[nextIndex];
    
    setCurrentCameraId(nextCamera.deviceId);
    
    // Update facing mode based on camera label
    if (nextCamera.label.toLowerCase().includes('front') || 
        nextCamera.label.toLowerCase().includes('user')) {
      setFacingMode('user');
    } else {
      setFacingMode('environment');
    }
    
    // Restart camera with new device
    if (cameraMode) {
      stopCameraPreview();
      setTimeout(() => startCameraPreview(), 100);
    }
  };

  // Stop camera preview
  const stopCameraPreview = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setCameraMode(false);
    setCapturedImage(null);
  };

  // Start camera preview
  const startCameraPreview = async () => {
    try {
      setUploadError(null);
      
      // Check if camera is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setUploadError('Camera not supported on this device or browser.');
        return;
      }

      // Detect available cameras first
      if (availableCameras.length === 0) {
        await detectCameras();
      }

      // Build constraints based on available cameras
      const constraints: MediaStreamConstraints = {
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      };

      // Use specific camera if available, otherwise use facingMode
      if (currentCameraId) {
        (constraints.video as MediaTrackConstraints).deviceId = { exact: currentCameraId };
      } else {
        (constraints.video as MediaTrackConstraints).facingMode = facingMode;
      }

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      setCameraStream(stream);
      setCameraMode(true);
      setCapturedImage(null);
      
      // Wait a bit for the modal to render the video element
      setTimeout(() => {
        const video = document.getElementById('cameraVideo') as HTMLVideoElement;
        if (video) {
          video.srcObject = stream;
          video.play().catch((err: Error) => {
            console.error('Error playing video:', err);
            setUploadError('Failed to start camera preview.');
          });
        } else {
          console.error('Video element not found');
          setUploadError('Camera preview element not found.');
        }
      }, 100);
      
    } catch (error: unknown) {
      console.error('Error accessing camera:', error);
      let errorMessage = 'Unable to access camera. ';
      
      if (error instanceof Error) {
        if (error.name === 'NotAllowedError') {
          errorMessage += 'Please allow camera permissions.';
        } else if (error.name === 'NotFoundError') {
          errorMessage += 'No camera found on this device.';
        } else if (error.name === 'NotReadableError') {
          errorMessage += 'Camera is already in use by another application.';
        } else {
          errorMessage += 'Please check camera permissions and try again.';
        }
      }
      
      setUploadError(errorMessage);
    }
  };

  const applyUploadWarnings = (result?: AddVehicleImagesResultDto) => {
    const warnings = result?.warnings?.filter(w => w.isNonVehicle) ?? [];
    if (!warnings.length) {
      setUploadWarning(null);
      return;
    }

    const message =
      warnings.length === 1
        ? warnings[0].message || 'Uploaded photo may not show a vehicle.'
        : `${warnings.length} uploaded photos may not show a vehicle.`;
    setUploadWarning(message);
    toast.warning(message, { ...toastConfig, autoClose: 6000 });
  };

  // Capture photo from camera and upload directly
  const capturePhoto = async () => {
    const video = document.getElementById('cameraVideo') as HTMLVideoElement;
    if (!video || !vehicleId) return;

    try {
      setUploading(true);
      setUploadError(null);
      setUploadWarning(null);

      // Create canvas to capture frame
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context?.drawImage(video, 0, 0);
      
      // Convert canvas to blob and create data URL for preview
      const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
      setCapturedImage(dataUrl);

      // Stop the camera feed after capturing
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }

      const blob = await new Promise<Blob>((resolve) => {
        canvas.toBlob((blob) => {
          resolve(blob!);
        }, 'image/jpeg', 0.8);
      });

      // Create file from blob
      const file = new File([blob], `vehicle_${vehicleId}_${Date.now()}.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now()
      });

      // Upload directly to backend
      const formData = new FormData();
      formData.append('files', file);

      const response = await axiosInstance.post<AddVehicleImagesResultDto>(
        `/api/v1/vehicles/images?vehicleId=${vehicleId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      // Success - close modal, refresh vehicle details
      setShowUploadModal(false);
      setUploadSuccess(true);
      applyUploadWarnings(response.data);
      
      // Refresh vehicle details to show new image
      await fetchVehicleDetails();

      // Hide success message after 3 seconds
      setTimeout(() => {
        setUploadSuccess(false);
      }, 3000);

    } catch (error: unknown) {
      console.error('Upload error:', error);
      setUploadError(error instanceof Error ? error.message : 'An error occurred while uploading the image');
    } finally {
      setUploading(false);
    }
  };

  // Handle file selection
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setSelectedFiles(Array.from(event.target.files));
    }
  };

  // Upload files
  const uploadFiles = async () => {
    if (!selectedFiles.length || !vehicleId) return;

    try {
      setUploading(true);
      setUploadError(null);
      setUploadWarning(null);

      const formData = new FormData();
      selectedFiles.forEach(file => {
        formData.append('files', file);
      });

      const response = await axiosInstance.post<AddVehicleImagesResultDto>(
        `/api/v1/vehicles/images?vehicleId=${vehicleId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      // Success - close modal, refresh vehicle details
      setShowUploadModal(false);
      setUploadSuccess(true);
      applyUploadWarnings(response.data);
      
      // Refresh vehicle details to show new images
      await fetchVehicleDetails();

      // Hide success message after 3 seconds
      setTimeout(() => {
        setUploadSuccess(false);
      }, 3000);

    } catch (error: unknown) {
      console.error('Upload error:', error);
      setUploadError(error instanceof Error ? error.message : 'An error occurred while uploading the files');
    } finally {
      setUploading(false);
      setSelectedFiles([]);
    }
  };

  // Reset modal state
  const resetModalState = () => {
    setSelectedFiles([]);
    setUploadError(null);
    setCameraMode(false);
    setCapturedImage(null);
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
  };

  // Reset upload modal
  const resetUploadModal = () => {
    resetModalState();
    setShowUploadModal(false);
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-3 text-muted">Loading vehicle details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="m-4">
        <Alert.Heading>Error Loading Vehicle</Alert.Heading>
        <p>{error}</p>
        <hr />
        <div className="d-flex justify-content-end">
          <Button variant="outline-danger" onClick={() => navigate('/vehicles')}>
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back to Vehicles
          </Button>
        </div>
      </Alert>
    );
  }

  if (!vehicle) {
    return (
      <Alert variant="warning" className="m-4">
        <Alert.Heading>Vehicle Not Found</Alert.Heading>
        <p>The requested vehicle could not be found.</p>
        <hr />
        <div className="d-flex justify-content-end">
          <Button variant="outline-warning" onClick={() => navigate('/vehicles')}>
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back to Vehicles
          </Button>
        </div>
      </Alert>
    );
  }

  const images = (vehicle.vehicleImages || []).filter(
    (img) =>
      img.vehicleImageType === VEHICLE_IMAGE_TYPE_VEHICLE ||
      img.vehicleImageType === 'Vehicle' ||
      img.vehicleImageCategoryName === 'Vehicle'
  );

  return (
    <div className="container-fluid">
      {/* Header */}
      <Row className="mb-3">
        <Col>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => navigate('/vehicles')}
            >
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              Back
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={openEditModal}
            >
              <FontAwesomeIcon icon={faEdit} className="me-2" />
              Edit Vehicle
            </Button>
          </div>
        </Col>
      </Row>

      {/* Success/Error Messages */}
      {uploadSuccess && (
        <Alert variant="success" dismissible onClose={() => setUploadSuccess(false)} className="mb-4">
          <FontAwesomeIcon icon={faUpload} className="me-2" />
          Images uploaded successfully!
        </Alert>
      )}

      {uploadWarning && (
        <Alert variant="warning" dismissible onClose={() => setUploadWarning(null)} className="mb-4">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          <strong>Photo warning:</strong> {uploadWarning}
        </Alert>
      )}
      
      {uploadError && (
        <Alert variant="danger" dismissible onClose={() => setUploadError(null)} className="mb-4">
          <strong>Upload Error:</strong> {uploadError}
        </Alert>
      )}

      {/* Main Content */}
      <Row className="g-3 mb-4">
        {/* Left: vehicle + customer */}
        <Col lg={4}>
          <Card className="mb-3">
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <FontAwesomeIcon icon={faCar} className="me-2" />
                  Vehicle
                </h5>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => {
                    resetModalState();
                    setShowUploadModal(true);
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-1" />
                  Photos
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              <div className="d-flex flex-column align-items-center align-items-lg-start gap-3 mb-3">
                <div
                  className="bg-light border rounded overflow-hidden d-flex align-items-center justify-content-center flex-shrink-0 position-relative"
                  style={{
                    width: 180,
                    height: 180,
                    cursor: images.length ? 'zoom-in' : 'default'
                  }}
                  onClick={() => {
                    if (images.length) {
                      handleImageClick(Math.min(selectedImageIndex, images.length - 1));
                    }
                  }}
                  title={images.length ? 'View vehicle photo' : 'No vehicle photo'}
                >
                  {images.length > 0 ? (
                    <>
                      <RetryImage
                        googleFileId={images[Math.min(selectedImageIndex, images.length - 1)].googleFileId}
                        alt={vehicle.plateNumber || vehicle.conductionNumber || 'Vehicle'}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      {images[Math.min(selectedImageIndex, images.length - 1)].hasNonVehicleWarning && (
                        <Badge
                          bg="warning"
                          text="dark"
                          title={
                            images[Math.min(selectedImageIndex, images.length - 1)].nonVehicleWarningReason ||
                            'This photo may not show a vehicle'
                          }
                          style={{ position: 'absolute', left: 8, bottom: 8 }}
                        >
                          <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                          Not a vehicle?
                        </Badge>
                      )}
                    </>
                  ) : (
                    <FontAwesomeIcon icon={faCar} size="3x" className="text-muted" />
                  )}
                </div>
                {images.length > 1 && (
                  <div className="d-flex flex-wrap gap-2 w-100 justify-content-center justify-content-lg-start">
                    {images.map((image, index) => (
                      <div
                        key={image.vehicleImageId}
                        className="position-relative"
                      >
                        <button
                          type="button"
                          className="border rounded overflow-hidden p-0 bg-white"
                          style={{
                            width: 44,
                            height: 44,
                            outline: index === selectedImageIndex ? '2px solid var(--phoenix-primary)' : undefined
                          }}
                          onClick={() => setSelectedImageIndex(index)}
                        >
                          <RetryImage
                            googleFileId={image.googleFileId}
                            alt={`Thumb ${index + 1}`}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        </button>
                        {image.hasNonVehicleWarning && (
                          <span
                            title={image.nonVehicleWarningReason || 'This photo may not show a vehicle'}
                            style={{
                              position: 'absolute',
                              left: -4,
                              bottom: -4,
                              color: '#b78103',
                              background: '#fff',
                              borderRadius: '50%',
                              width: 16,
                              height: 16,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 10,
                              boxShadow: '0 0 0 1px rgba(0,0,0,0.1)'
                            }}
                          >
                            <FontAwesomeIcon icon={faExclamationTriangle} />
                          </span>
                        )}
                        <Button
                          size="sm"
                          variant="danger"
                          style={{
                            position: 'absolute',
                            top: -6,
                            right: -6,
                            padding: '0 4px',
                            lineHeight: 1
                          }}
                          onClick={event => {
                            event.stopPropagation();
                            void handleDeleteImage(image);
                          }}
                          title="Delete photo"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                {images.length === 1 && (
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => void handleDeleteImage(images[0])}
                  >
                    <FontAwesomeIcon icon={faTrash} className="me-1" />
                    Delete photo
                  </Button>
                )}
              </div>

              <Table borderless size="sm" className="mb-0">
                <tbody>
                  <tr>
                    <td style={{ width: '40%' }}><strong>Plate:</strong></td>
                    <td>{vehicle.plateNumber || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td><strong>Conduction:</strong></td>
                    <td>{vehicle.conductionNumber || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td><strong>Chassis:</strong></td>
                    <td>{vehicle.chassisNo || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td><strong>Make / Model:</strong></td>
                    <td>{vehicle.carMake} {vehicle.carModel}</td>
                  </tr>
                  <tr>
                    <td><strong>Body type:</strong></td>
                    <td>{carModelBodyTypeLabel(vehicle.bodyType) || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td><strong>Year:</strong></td>
                    <td>{vehicle.year}</td>
                  </tr>
                  <tr>
                    <td><strong>Color:</strong></td>
                    <td>{vehicle.color || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td><strong>Fuel:</strong></td>
                    <td>{vehicle.fuelType || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td><strong>Transmission:</strong></td>
                    <td>{vehicle.transmission || 'N/A'}</td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          <Card className="mb-3 mb-lg-0">
            <Card.Header>
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faUser} className="me-2" />
                Customer
              </h5>
            </Card.Header>
            <Card.Body>
              {vehicle.customerId || vehicle.customerName ? (
                <Table borderless size="sm" className="mb-0">
                  <tbody>
                    {vehicle.customerId != null && (
                      <tr>
                        <td style={{ width: '40%' }}><strong>Customer ID:</strong></td>
                        <td>{vehicle.customerId}</td>
                      </tr>
                    )}
                    <tr>
                      <td><strong>Name:</strong></td>
                      <td>{vehicle.customerName || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td><strong>Referrer:</strong></td>
                      <td>
                        <div className="d-flex align-items-start justify-content-between gap-2">
                          <div className="flex-grow-1">
                            {vehicle.customerReferrer?.trim()
                              ? vehicle.customerReferrer
                              : <span className="text-muted">Not set</span>}
                          </div>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="flex-shrink-0"
                            title="Edit referrer"
                            onClick={() => {
                              setEditReferrer(vehicle.customerReferrer ?? '');
                              setShowEditReferrerModal(true);
                            }}
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </Table>
              ) : (
                <p className="text-muted mb-0">No customer linked to this vehicle.</p>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Right: service history */}
        <Col lg={8}>
          <Card className="h-100">
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <FontAwesomeIcon icon={faWrench} className="me-2" />
                  Service History
                </h5>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setShowServiceModal(true)}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Add Service
                </Button>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {servicesLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" />
                  <span className="ms-2">Loading services...</span>
                </div>
              ) : services.length > 0 ? (
                <ListGroup variant="flush">
                  {/* Newest first */}
                  {[...services]
                    .sort((a, b) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime())
                    .map((service) => {
                      const title =
                        service.serviceTitle
                        || service.customerRequestComplaintPlainText
                        || extractComplaintPlainText(service.customerRequestComplaint)
                        || 'Service visit';
                      const statusBg =
                        service.status === 'New' ? 'warning'
                        : service.status === 'InProgress' ? 'info'
                        : 'success';
                      const statusLabel = service.status === 'InProgress' ? 'In Progress' : service.status;
                      const created = new Date(service.createdOn);

                      return (
                        <ListGroup.Item key={service.vehicleServiceId} className="px-3 py-3">
                          <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-1">
                            <div>
                              <div className="fw-semibold">{title}</div>
                              <div className="text-muted small">
                                {created.toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric'
                                })}
                                {' · '}
                                <Badge
                                  bg={service.mileageMissing || service.currentMileage <= 0 ? 'warning' : 'secondary'}
                                  className="align-middle"
                                >
                                  {(service.mileageMissing || service.currentMileage <= 0) && (
                                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                                  )}
                                  {service.currentMileage.toLocaleString()} km
                                </Badge>
                              </div>
                            </div>
                            <div className="d-flex flex-wrap gap-1 align-items-center">
                              <Badge bg={statusBg}>{statusLabel}</Badge>
                              {service.status === 'Submitted' && (
                                <Badge bg={service.paymentStatus === 'Paid' ? 'success' : 'secondary'}>
                                  {service.paymentStatus === 'Paid'
                                    ? `Paid${service.paymentMethodDisplay ? ` · ${service.paymentMethodDisplay}` : ''}`
                                    : 'Unpaid'}
                                </Badge>
                              )}
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => navigate(`/vehicle-service/${service.vehicleServiceId}`)}
                              >
                                View
                              </Button>
                              {service.hasServiceSummary && (
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  title="View summary PDF"
                                  onClick={() => void handleViewSummary(service.vehicleServiceId)}
                                >
                                  <FontAwesomeIcon icon={faFilePdf} className="me-1" />
                                  Summary
                                </Button>
                              )}
                              <Button
                                variant="outline-dark"
                                size="sm"
                                title="View PDF"
                                onClick={() => void handleDownloadInvoice(service.vehicleServiceId)}
                              >
                                <FontAwesomeIcon icon={faFilePdf} className="me-1" />
                                PDF
                              </Button>
                            </div>
                          </div>
                        </ListGroup.Item>
                      );
                    })}
                </ListGroup>
              ) : (
                <div className="text-center py-4 text-muted">
                  <p className="mb-0">No service records found for this vehicle.</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Image Modal */}
      <Modal show={showImageModal} onHide={() => setShowImageModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            Vehicle Images ({Math.min(selectedImageIndex, Math.max(images.length - 1, 0)) + 1} of {images.length})
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {images.length > 0 && (
            <>
              {images[Math.min(selectedImageIndex, images.length - 1)].hasNonVehicleWarning && (
                <Alert variant="warning" className="text-start mb-3">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                  {images[Math.min(selectedImageIndex, images.length - 1)].nonVehicleWarningReason ||
                    'This photo may not show a vehicle.'}
                </Alert>
              )}
              <Carousel
                activeIndex={Math.min(selectedImageIndex, images.length - 1)}
                onSelect={(index) => setSelectedImageIndex(index)}
                interval={null}
                indicators={images.length > 1}
                controls={images.length > 1}
              >
                {images.map((image: VehicleImageDto, index: number) => (
                  <Carousel.Item key={image.vehicleImageId}>
                    <div
                      style={{
                        minHeight: '50vh',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px'
                      }}
                    >
                      <RetryImage
                        googleFileId={image.googleFileId}
                        alt={`Vehicle ${index + 1}`}
                        fluid
                        style={{
                          maxHeight: '70vh',
                          transform:
                            index === selectedImageIndex
                              ? `rotate(${imageRotation.rotationDegrees}deg)`
                              : undefined,
                          transition: 'transform 0.2s ease'
                        }}
                      />
                    </div>
                  </Carousel.Item>
                ))}
              </Carousel>
            </>
          )}
        </Modal.Body>
        {images.length > 0 && (
          <Modal.Footer className="d-flex justify-content-between flex-wrap gap-2">
            <ImageRotateControls
              googleFileId={currentVehiclePhoto?.googleFileId}
              rotationDegrees={imageRotation.rotationDegrees}
              saving={imageRotation.saving}
              onRotate={imageRotation.rotateBy}
              onSave={() => void imageRotation.saveRotation()}
            />
            <Button
              variant="outline-danger"
              onClick={() =>
                void handleDeleteImage(
                  images[Math.min(selectedImageIndex, images.length - 1)]
                )
              }
            >
              <FontAwesomeIcon icon={faTrash} className="me-2" />
              Delete photo
            </Button>
          </Modal.Footer>
        )}
      </Modal>

      {/* Upload Modal */}
      <Modal show={showUploadModal} onHide={resetUploadModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Upload Vehicle Images</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {uploadError && (
            <Alert variant="danger" dismissible onClose={() => setUploadError(null)} className="mb-3">
              <strong>Error:</strong> {uploadError}
            </Alert>
          )}

          {/* Camera Preview */}
          {cameraMode && (
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Camera Preview</h6>
                {availableCameras.length > 1 && (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={switchCamera}
                    disabled={uploading}
                  >
                    <FontAwesomeIcon icon={faSync} className="me-2" />
                    Switch Camera
                  </Button>
                )}
              </div>
              <div className="text-center">
                {capturedImage ? (
                  <>
                    <img
                      src={capturedImage}
                      alt="Captured"
                      style={{
                        width: '100%',
                        maxWidth: '400px',
                        height: 'auto',
                        borderRadius: '8px',
                        border: '2px solid #dee2e6'
                      }}
                    />
                    <div className="mt-3 text-muted">
                      {uploading ? 'Uploading photo...' : 'Photo captured and uploading...'}
                    </div>
                  </>
                ) : (
                  <>
                    <video
                      id="cameraVideo"
                      style={{
                        width: '100%',
                        maxWidth: '400px',
                        height: 'auto',
                        borderRadius: '8px',
                        border: '2px solid #dee2e6'
                      }}
                      autoPlay
                      playsInline
                      muted
                    />
                    <div className="mt-3 d-flex justify-content-center gap-2">
                      <Button
                        variant="primary"
                        size="lg"
                        onClick={capturePhoto}
                        disabled={uploading}
                      >
                        <FontAwesomeIcon icon={faCamera} className="me-2" />
                        Take Photo & Upload
                      </Button>
                      <Button
                        variant="outline-secondary"
                        onClick={stopCameraPreview}
                        disabled={uploading}
                      >
                        Cancel
                      </Button>
                    </div>
                    {availableCameras.length > 1 && (
                      <div className="mt-2 text-muted small">
                        Using: {availableCameras.find(cam => cam.deviceId === currentCameraId)?.label || 'Default Camera'}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* File Selection Options */}
          {!cameraMode && (
            <div className="mb-4">
              <h6 className="mb-3">Choose an option:</h6>
              <ButtonGroup className="w-100 mb-3">
                <Button
                  variant="outline-primary"
                  onClick={startCameraPreview}
                  disabled={uploading}
                >
                  <FontAwesomeIcon icon={faCamera} className="me-2" />
                  Use Camera
                </Button>
                <Button
                  variant="outline-primary"
                  onClick={() => document.getElementById('fileInput')?.click()}
                  disabled={uploading}
                >
                  <FontAwesomeIcon icon={faUpload} className="me-2" />
                  Choose Files
                </Button>
              </ButtonGroup>
              
              <Form.Control
                id="fileInput"
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </div>
          )}

          {/* Selected Files Preview */}
          {selectedFiles.length > 0 && (
            <div className="mb-4">
              <h6 className="mb-3">Selected Files ({selectedFiles.length}):</h6>
              <div className="row g-2">
                {selectedFiles.map((file, index) => (
                  <div key={index} className="col-6 col-md-4">
                    <div className="card">
                      <div className="card-body p-2 text-center">
                        <div className="text-truncate small">{file.name}</div>
                        <div className="text-muted small">{(file.size / 1024 / 1024).toFixed(2)} MB</div>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="mt-1"
                          onClick={() => {
                            const newFiles = selectedFiles.filter((_, i) => i !== index);
                            setSelectedFiles(newFiles);
                          }}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={resetUploadModal} disabled={uploading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={uploadFiles}
            disabled={selectedFiles.length === 0 || uploading}
          >
            {uploading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Uploading...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faUpload} className="me-2" />
                Upload {selectedFiles.length} File{selectedFiles.length !== 1 ? 's' : ''}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Referrer Modal */}
      <Modal show={showEditReferrerModal} onHide={() => { if (!savingReferrer) setShowEditReferrerModal(false); }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Referrer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={(event) => {
            event.preventDefault();
            void (async () => {
              if (!vehicleId) return;
              try {
                setSavingReferrer(true);
                const response = await axiosInstance.put<{ customerReferrer?: string | null }>(
                  `/api/v1/vehicles/${vehicleId}/customer-referrer`,
                  { referrer: editReferrer.trim() || null }
                );
                setVehicle(current => current ? { ...current, customerReferrer: response.data.customerReferrer ?? null } : current);
                setShowEditReferrerModal(false);
                toast.success('Referrer updated', toastConfig);
              } catch (err) {
                const message = err instanceof AxiosError
                  ? (err.response?.data as { errorMessage?: string } | undefined)?.errorMessage
                  : undefined;
                toast.error(message || 'Could not save the referrer', toastConfig);
              } finally {
                setSavingReferrer(false);
              }
            })();
          }}>
            <Form.Group className="mb-3">
              <Form.Label>Referrer</Form.Label>
              <Form.Control
                type="text"
                value={editReferrer}
                maxLength={100}
                disabled={savingReferrer}
                placeholder="Who referred this customer?"
                onChange={event => setEditReferrer(event.target.value)}
              />
            </Form.Group>
            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={() => setShowEditReferrerModal(false)} disabled={savingReferrer}>
                Cancel
              </Button>
              <Button variant="success" type="submit" disabled={savingReferrer}>
                {savingReferrer ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Edit Vehicle Modal */}
      <Modal show={showEditModal} onHide={closeEditModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Vehicle</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editError && (
            <Alert variant="danger" dismissible onClose={() => setEditError(null)} className="mb-3">
              <strong>Error:</strong> {editError}
            </Alert>
          )}

          <Form onSubmit={handleEditSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Plate Number <small className="text-muted">(at least one required)</small></Form.Label>
                  <Form.Control
                    type="text"
                    name="PlateNumber"
                    value={editFormData.PlateNumber || ''}
                    onChange={handleEditInputChange}
                    placeholder="Enter plate number"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Conduction Number <small className="text-muted">(at least one required)</small></Form.Label>
                  <Form.Control
                    type="text"
                    name="ConductionNumber"
                    value={editFormData.ConductionNumber || ''}
                    onChange={handleEditInputChange}
                    placeholder="Enter conduction number"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <CarMakeSelect
                  value={editFormData.CarMake}
                  onChange={handleEditInputChange}
                />
              </Col>
              <Col md={6}>
                <CarModelSelect
                  value={editFormData.CarModel}
                  carMake={editFormData.CarMake}
                  onChange={handleEditInputChange}
                />
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <CarModelBodyTypeSelect
                  value={editFormData.BodyType}
                  onChange={handleEditInputChange}
                />
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Year *</Form.Label>
                  <Form.Control
                    type="number"
                    name="Year"
                    value={editFormData.Year}
                    onChange={handleEditInputChange}
                    required
                    min="1900"
                    max={new Date().getFullYear() + 1}
                    placeholder="Enter year"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Chassis Number</Form.Label>
                  <Form.Control
                    type="text"
                    name="ChassisNo"
                    value={editFormData.ChassisNo}
                    onChange={handleEditInputChange}
                    placeholder="Enter chassis number"
                  />
                </Form.Group>
              </Col>
            </Row>

                         <Row>
               <Col md={6}>
                 <Form.Group className="mb-3">
                   <Form.Label>Color</Form.Label>
                   <Form.Control
                     type="text"
                     name="Color"
                     value={editFormData.Color}
                     onChange={handleEditInputChange}
                     placeholder="Enter color"
                   />
                 </Form.Group>
               </Col>
             </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Fuel Type</Form.Label>
                  <Form.Select
                    name="FuelType"
                    value={editFormData.FuelType}
                    onChange={handleEditInputChange}
                  >
                    <option value="">Select fuel type</option>
                    <option value="Petrol">Petrol</option>
                    <option value="Diesel">Diesel</option>
                    <option value="Electric">Electric</option>
                    <option value="Hybrid">Hybrid</option>
                    <option value="CNG">CNG</option>
                    <option value="LPG">LPG</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Transmission</Form.Label>
                  <Form.Select
                    name="Transmission"
                    value={editFormData.Transmission}
                    onChange={handleEditInputChange}
                  >
                    <option value="">Select transmission</option>
                    <option value="Manual">Manual</option>
                    <option value="Automatic">Automatic</option>
                    <option value="CVT">CVT</option>
                    <option value="Semi-Automatic">Semi-Automatic</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Customer</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control
                  type="text"
                  value={selectedCustomer?.customerName || ''}
                  readOnly
                  placeholder="No customer selected"
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowCustomerModal(true)}
                >
                  <FontAwesomeIcon icon={faSearch} />
                </Button>
                {selectedCustomer && (
                  <Button
                    variant="outline-danger"
                    onClick={() => {
                      setSelectedCustomer(null);
                      setEditFormData(prev => ({ ...prev, CustomerId: undefined }));
                    }}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </Button>
                )}
              </div>
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={closeEditModal} disabled={editLoading}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={editLoading}>
                {editLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Customer Selection Modal */}
      <Modal show={showCustomerModal} onHide={() => setShowCustomerModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Select Customer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Search Customer</Form.Label>
            <Form.Control
              type="text"
              placeholder="Search by name, email, or phone..."
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
            />
          </Form.Group>

          {customerLoading && (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" />
              <span className="ms-2">Searching...</span>
            </div>
          )}

          {customers.length > 0 && (
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer) => (
                  <tr key={customer.customerId}>
                    <td>{customer.customerName}</td>
                    <td>{customer.email}</td>
                    <td>{customer.contactNumber}</td>
                    <td>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleCustomerSelect(customer)}
                      >
                        Select
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          {customerSearch && !customerLoading && customers.length === 0 && (
            <div className="text-center py-3 text-muted">
              No customers found matching your search.
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Add Service Modal */}
      <Modal show={showServiceModal} onHide={() => setShowServiceModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Service Record</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={(e) => {
            e.preventDefault();
            handleCreateService();
          }}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Current Mileage <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    value={serviceFormData.currentMileage}
                    onChange={(e) => setServiceFormData({
                      ...serviceFormData,
                      currentMileage: parseInt(e.target.value) || 0
                    })}
                    required
                  />
                  {serviceFormData.currentMileage <= 0 && (
                    <Form.Text className="text-warning">
                      <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                      Mileage is empty or zero.
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Title <span className="text-muted">(optional)</span></Form.Label>
              <Form.Control
                type="text"
                maxLength={80}
                value={serviceFormData.serviceTitle || ''}
                onChange={(e) => setServiceFormData({
                  ...serviceFormData,
                  serviceTitle: e.target.value
                })}
                placeholder="Leave blank to auto-generate from the concern"
              />
              <Form.Text className="text-muted">
                {(serviceFormData.serviceTitle || '').trim().length}/80 — if empty, AI generates a short title.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Customer concern <span className="text-danger">*</span></Form.Label>
              <ComplaintEditor
                value={serviceFormData.customerRequestComplaint}
                onChange={(html: string) => setServiceFormData({
                  ...serviceFormData,
                  customerRequestComplaint: html
                })}
                maxPlainTextLength={500}
              />
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button 
                variant="secondary" 
                onClick={() => setShowServiceModal(false)}
                disabled={serviceLoading}
              >
                Cancel
              </Button>
              <Button 
                variant="primary" 
                type="submit"
                disabled={
                  serviceLoading
                  || !extractComplaintPlainText(serviceFormData.customerRequestComplaint)
                  || extractComplaintPlainText(serviceFormData.customerRequestComplaint).length > 500
                }
              >
                {serviceLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Adding...
                  </>
                ) : (
                  'Add Service'
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <HtmlPdfPreviewModal
        show={showInvoicePreview}
        onHide={closeInvoicePreview}
        title="PDF"
        preview={invoicePreview}
        isLoading={generatingInvoice}
      />

      <Modal show={showMileageConfirmModal} onHide={() => !serviceLoading && setShowMileageConfirmModal(false)} centered>
        <Modal.Header closeButton={!serviceLoading}>
          <Modal.Title>
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning me-2" />
            Mileage is empty
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Mileage is empty or zero. Continue creating this service?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowMileageConfirmModal(false)} disabled={serviceLoading}>
            Cancel
          </Button>
          <Button variant="warning" onClick={() => void createService()} disabled={serviceLoading}>
            {serviceLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Creating...
              </>
            ) : (
              'Continue'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default VehicleDetails; 